import { FC, useState } from 'react';
import { Autocomplete, Checkbox, Chip, TextField, TextFieldProps } from '@mui/material';
import { Code } from '../../../types/Code';
import { useCodeList } from '../../../hooks/Models/CodeList';

type CampaignStrategyTypeSetFieldProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
    value: Code[];
    onChange: (values: Code[]) => void;
    displayEmpty?: boolean;
    label?: string;
};

const CampaignStrategyTypeSetField: FC<CampaignStrategyTypeSetFieldProps> = ({
    value,
    onChange,
    displayEmpty = false,
    label = 'Campaign Strategy',
    ...props
}) => {
    const { codes } = useCodeList({ codeGroup: 'StrategyType' });
    const [isFocused, setIsFocused] = useState(false);

    return (
        <Autocomplete
            multiple
            options={codes}
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            getOptionLabel={(option) => option.description || option.code}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            renderTags={(tagValue, getTagProps) => {
                const count = tagValue.length;

                if (isFocused) return null;

                if (count > 1) {
                    return [
                        <Chip
                            key="chip-summary"
                            label={`${count} Selected`}
                            size="small"
                            onDelete={() => onChange([])}
                        />,
                    ];
                }

                if (count === 1) {
                    return [
                        <Chip
                            label={tagValue[0].description || tagValue[0].code}
                            size="small"
                            {...getTagProps({ index: 0 })}
                        />,
                    ];
                }

                return null;
            }}
            renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option.description || option.code}
                </li>
            )}
            renderInput={(params) => <TextField {...params} label={label} size="small" {...props} />}
            disableCloseOnSelect
            size="small"
        />
    );
};

export type { CampaignStrategyTypeSetFieldProps };
export default CampaignStrategyTypeSetField;
